import {
  Flex,
  Typography,
  type TableColumnsType,
  type TablePaginationConfig
} from "antd"
import type { FilterValue, SorterResult } from "antd/es/table/interface"
import React, { useCallback, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"

import { CardComponent } from "../../../../../components/Card/CardComponent"
import { EntityTable } from "../../../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../../components/EntityTable/surfaces.ts"
import { SearchBar } from "../../../../../components/Input/SearchBar.tsx"
import { ResolvedSelect } from "../../../../../components/Select/ResolvedSelect.tsx"
import { TableResultCount } from "../../../../../components/Table/TableResultCount.tsx"
import { useScrolledTable } from "../../../../../hooks/use-scrolled-table.ts"
import { getTableOrderingFromSorter } from "../../../../../utils/table"
import { LeadsEmpty } from "../../../components/LeadsEmpty.tsx"
import { INACTIVE_REASONS } from "../../../../Lead/constants/operational-states.ts"
import { narrowsInactiveFilters } from "../../../redux/leads.constants.ts"
import type { LeadsFilters } from "../../../redux/leads.types.ts"
import type {
  InactiveCounts,
  LeadEmailCampaignRequest,
  LeadEmailCampaignResult
} from "../../../api/services.types.ts"
import { type LeadListItem } from "../../../types/types.ts"
import { getColumns } from "./columns.tsx"
import { InactiveSmartFilters } from "./InactiveSmartFilters.tsx"
import { LeadsInactiveBulkBar } from "./LeadsInactiveBulkBar.tsx"

const { Text } = Typography

interface LeadsInactiveTableProps {
  loading: boolean
  filters: LeadsFilters
  leads: LeadListItem[]
  count: number
  counts?: InactiveCounts | null
  canSeeWon?: boolean
  next?: string | null
  retryNext?: string | null
  onLoadMore: () => void
  onOrderingChange: (ordering: string | null) => void
  onSelectRow: (record: LeadListItem, action: string, payload?: unknown) => void
  onSearchChange?: (value?: string | null) => void
  onFiltersChange: (filters: Partial<LeadsFilters>) => void
  //
  ordering: string | null
  editAllowed?: boolean
  deleteAllowed?: boolean
  selectedIds?: string[]
  onSelectedIdsChange?: (ids: string[]) => void
  onSendEmail: (params: LeadEmailCampaignRequest) => Promise<LeadEmailCampaignResult>
}

export default function LeadsInactiveTable({
  loading,
  filters,
  leads,
  count,
  counts,
  canSeeWon,
  next,
  retryNext,
  onLoadMore,
  onOrderingChange,
  onSelectRow,
  onSearchChange,
  onFiltersChange,
  editAllowed,
  deleteAllowed,
  selectedIds = [],
  onSelectedIdsChange,
  onSendEmail
}: LeadsInactiveTableProps) {
  const { t } = useTranslation()
  const reasonOptions = INACTIVE_REASONS.filter(
    (reason) => reason.state !== "won" || canSeeWon
  ).map((reason) => ({ value: reason.state, label: t(reason.label) }))
  const onChangeTable = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<LeadListItem> | SorterResult<LeadListItem>[]
    ) => {
      const ordering = getTableOrderingFromSorter(sorter)

      onOrderingChange(ordering)
    },
    [onOrderingChange]
  )

  const { tableRef, tableHeight, onTableScroll } = useScrolledTable({
    loading: loading,
    next: next,
    loadMore: onLoadMore,
    fullPage: true
  })

  const tableSettings = useMemo(
    () => ({
      scroll: {
        x: 1300,
        scrollToFirstRowOnChange: true
      },
      onRow: (record: LeadListItem) => {
        return {
          record,
          onSelectRow,
          onDoubleClick: (event: React.MouseEvent<HTMLElement>) => {
            // Toggling the checkbox quickly reads as a double-click on the row.
            const target = event.target as HTMLElement
            if (target?.closest?.(".ant-table-selection-column")) return
            onSelectRow(record, "view_details")
          }
        }
      }
    }),
    [onSelectRow]
  )

  const tableColumns: TableColumnsType<LeadListItem> = useMemo(
    () => getColumns({ onSelectRow, editAllowed, deleteAllowed }),
    [onSelectRow, editAllowed, deleteAllowed]
  )

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedIds,
      // The list is server-paginated with infinite scroll, so a row scrolled
      // past is no longer in dataSource — without this antd drops it from the
      // selection and the send silently loses recipients.
      preserveSelectedRowKeys: true,
      onChange: (keys: React.Key[]) => onSelectedIdsChange?.(keys.map(String))
    }),
    [selectedIds, onSelectedIdsChange]
  )

  // Accumulated across pages, not rebuilt from the loaded one: the selection
  // outlives a page, so a name looked up only in `leads` is a raw UUID for
  // exactly the rows preserveSelectedRowKeys kept.
  const seenLeadNames = useRef<Record<string, string>>({})
  const leadNames = useMemo(() => {
    const next = { ...seenLeadNames.current }
    for (const lead of leads ?? []) {
      if (lead?.id) next[lead.id] = lead.lead_name ?? lead.id
    }
    seenLeadNames.current = next
    return next
  }, [leads])

  return (
    <CardComponent classNames={{ body: "p-0" }}>
      <div className={"border-b border-gray-200"}>
        <InactiveSmartFilters
          filters={filters}
          counts={counts}
          canSeeWon={canSeeWon}
          disabled={loading}
          onFiltersChange={onFiltersChange}
        />
      </div>
      {onSearchChange && (
        <Flex
          wrap="wrap"
          gap="small"
          align="end"
          className="py-4 md:p-4 border-b border-gray-200"
        >
          <SearchBar
            placeholder="Search leads"
            defaultValue={filters?.q || ""}
            onSearch={onSearchChange}
          />
          <Flex vertical gap={4} className={"min-w-0 w-56"}>
            <Text type={"secondary"} className={"text-xs font-medium"}>
              {t("Inactive Reason")}
            </Text>
            <ResolvedSelect
              maxTagCount={"responsive"}
              mode={"multiple"}
              placeholder={t("All reasons")}
              value={filters?.reason}
              onChange={(value) => onFiltersChange({ reason: value || null })}
              allowClear
              options={reasonOptions}
              disabled={loading}
            />
          </Flex>
          {narrowsInactiveFilters(filters) && (
            <Text
              type={"secondary"}
              className={
                "cursor-pointer self-center text-xs font-medium underline"
              }
              onClick={() => onFiltersChange({ reason: null })}
            >
              {t("Clear All")}
            </Text>
          )}
        </Flex>
      )}
      <div ref={tableRef}>
        <EntityTable<LeadListItem>
          surface={TABLE_SURFACES.leadsInactive}
          selectionMode="on-demand"
          selectionFilters={filters}
          tab={DEFAULT_TAB}
          columns={tableColumns}
          loading={loading && !leads?.length}
          dataSource={leads}
          scroll={{ ...tableSettings.scroll, y: tableHeight }}
          rowSelection={rowSelection}
          onChange={onChangeTable}
          onScroll={onTableScroll}
          withOutPagination
          pagination={false}
          onRow={tableSettings.onRow}
          rowHoverable={true}
          locale={{
            emptyText: <LeadsEmpty hasSearch={!!filters?.q} />
          }}
        />
        <TableResultCount
          loaded={leads?.length ?? 0}
          total={count}
          loading={loading}
          next={next}
          retryNext={retryNext}
          onRetry={onLoadMore}
        />
        <LeadsInactiveBulkBar
          selectedIds={selectedIds}
          leadNames={leadNames}
          editAllowed={editAllowed}
          onClear={() => onSelectedIdsChange?.([])}
          onSend={onSendEmail}
        />
      </div>
    </CardComponent>
  )
}
